import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchBasicPost, fetchBasicSocialPost, fetchInfluencerInsights as fetchInfluencerInsightsAPI, fetchMedia as fetchMediaAPI, fetchPost as fetchPostAPI, fetchSocialMedia, fetchSocialPost } from "services/api/influencer";
import { fetchSocialAccount } from "services/api/social-account";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { BasicSocialPost, SocialPost } from "types/campaign/collaborations";
import { InfluencerInsights, influencerSlice, Post, Posts, SocialAccount } from "types/influencer";
import { getErrorMessage, isFresh } from "utils/helpers";

export type SocialMediaPostType = 'REELS' | 'FEED' | 'VIDEO';

export const getSocialMediaCacheKey = (
    type: SocialMediaPostType,
    params?: { sort?: string; has_brand_collabs?: 'y' }
) => {
    if (params?.has_brand_collabs === 'y') {
        return `${type}_brand_collabs`;
    }
    if (params?.sort && params.sort !== 'like_count') {
        return `${type}_${params.sort}`;
    }
    return type;
};

export const fetchInfluencerInsights = createAsyncThunk<{influencerId: string, insightsData: InfluencerInsights}, {force?: boolean, influencerId: string}, {rejectValue: string}>(
    'influencer/insights',
    async ({force, influencerId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_INFLUENCER_INSIGHTS.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!force && isFresh(state.influencers.insights?.[influencerId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const insightsData = await fetchInfluencerInsightsAPI(agencyId, influencerId);

            return {insightsData, influencerId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch influencer insights"));
        }
    }
) 


export const fetchPost = createAsyncThunk<{influencerId: string, post: Post, postId: string}, {force?: boolean, influencerId: string, postId: string}, {rejectValue: string}>(
    'influencer/post',
    async ({force, influencerId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_INFLUENCER_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!force && isFresh(state.influencers.posts?.[influencerId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchPostAPI(agencyId, influencerId, postId);

            return {post, postId, influencerId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchBasicPostThunk = createAsyncThunk<{post: Post, postId: string}, {force?: boolean, postId: string}, {rejectValue: string}>(
    'influencer/basicPost',
    async ({force, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_INFLUENCER_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!force && isFresh(state.influencers.basic_posts?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchBasicPost(agencyId, postId);

            return {post, postId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 

export const fetchMedia = createAsyncThunk<{influencerId: string, posts: {items: Post[], count: number}, mediaProductType: 'reels' | 'posts', page: number}, {influencerId: string, mediaProductType : "reels" | "posts", page: number, force?: boolean }, {rejectValue: string}>(
    'influencer/media',
    async ({force, influencerId, mediaProductType, page}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const STALE_TIME = apiConfig.FETCH_INFLUENCER_MEDIA.staleTime;

            if(page === 1 && !force && isFresh(state.influencers.media?.[influencerId]?.[mediaProductType]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const agencyId = state.auth.selectedAgencyId ?? '';
            const posts = await fetchMediaAPI(agencyId, influencerId, {page, sort: 'timestamp', media_product_type: mediaProductType});

            return {posts, influencerId, mediaProductType, page}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch media!"))
        }
    }
)

export const fetchSocialPostsThunk = createAsyncThunk<
    { socialAccountId: string; posts: { items: SocialPost[]; count: number }; cacheKey: string; page: number },
    {
        socialAccountId: string;
        type: SocialMediaPostType;
        page: number;
        force?: boolean;
        sort?: string;
        limit?: number;
        has_brand_collabs?: 'y';
    },
    { rejectValue: string }
>(
    'social-account/social-posts',
    async ({ force, socialAccountId, type, page, sort = 'like_count', limit, has_brand_collabs }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState; influencers: influencerSlice };
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POSTS.staleTime;
            const cacheKey = getSocialMediaCacheKey(type, { sort, has_brand_collabs });

            if (page === 1 && !force && isFresh(state.influencers.social_media?.[socialAccountId]?.[cacheKey]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const agencyId = state.auth.selectedAgencyId ?? '';
            const posts = await fetchSocialMedia(agencyId, socialAccountId, {
                page,
                sort,
                limit,
                ...(has_brand_collabs ? { has_brand_collabs } : {}),
                ...(type !== 'VIDEO' ? { type } : {}),
            });

            return { posts, socialAccountId, cacheKey, page };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch media!"));
        }
    }
)

export const fetchInfluencerSocialAccount = createAsyncThunk<{socialAccountId: string, socialAccount: SocialAccount}, {socialAccountId: string, force?: boolean}, {rejectValue: string}>(
    'influencer/social-account',
    async ({socialAccountId, force=false}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT.staleTime;

            if(!force && isFresh(state.influencers.social_accounts?.[socialAccountId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }


            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!agencyId) throw new Error("No agency id selected!");
            const {social_account} = await fetchSocialAccount(agencyId, socialAccountId);

            return {socialAccountId, socialAccount: social_account}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account'));
        }
    }
)

export const fetchSocialPostThunk = createAsyncThunk<{socialAccountId: string, post: SocialPost, postId: string}, {force?: boolean, socialAccountId: string, postId: string}, {rejectValue: string}>(
    'social-account/social-post',
    async ({force, socialAccountId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!force && isFresh(state.influencers.social_posts?.[socialAccountId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchSocialPost(agencyId, socialAccountId, postId);

            return {post, postId, socialAccountId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 

export const fetchBasicSocialPostThunk = createAsyncThunk<{post: BasicSocialPost, postId: string}, {force?: boolean, postId: string}, {rejectValue: string}>(
    'influencer/basic-social-post',
    async ({force, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_BASIC_SOCIAL_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!force && isFresh(state.influencers.basic_social_posts?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchBasicSocialPost(agencyId, postId);

            return {post, postId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 